import React from 'react'
import ErrorBanner from './ErrorBanner'
import { Trans } from 'react-i18next'
import { Typography } from '@mui/material'
import { format } from '@e3dc-react/shell/Libs/date'
import { DeviceLinkModel } from 'src/Libs/installations/models'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { useInstallationHistory } from 'src/Libs/installations/hooks/use-installation-history'

interface SystemDecommissionedBannerProps {
  storage: DeviceLinkModel | undefined
  installationId: string
}

/**
 * Displays a warning banner if a service-user is looking at a decommissioned system/storage
 * @param storage - the device link corresponding to the storage
 * @param installationId
 * @returns
 */
const SystemDecommissionedBanner: React.FC<SystemDecommissionedBannerProps> = ({ storage, installationId }) => {
  const { t } = useCustomTranslation()
  const [{ data: deviceLinkHistory, hasError }] = useInstallationHistory(installationId)
  const deletionEntry = deviceLinkHistory?.find(entry => entry.action === 'REMOVED')

  const decommissionedBy = deletionEntry?.userName ?? t('system-details.deleted.unknown-user')
  const decommissioningReason = deletionEntry?.reason ?? t('system-details.deleted.unknown-reason')

  if (hasError) {
    return (
      <ErrorBanner visible={!!storage?.decommissionedAt}>
        <Typography>{t('system-details.deleted.error')}</Typography>
      </ErrorBanner>
    )
  }

  return (
    <ErrorBanner visible={!!storage?.decommissionedAt}>
      <Trans
        i18nKey="system-details.deleted.message"
        components={{
          deletedAt: <Typography component="strong" sx={{ fontWeight: 'bold' }} />,
          deletedBy: <Typography component="strong" sx={{ fontWeight: 'bold' }} />,
        }}
        values={{
          deletedAt: storage?.decommissionedAt && format(new Date(storage.decommissionedAt), t('common.format.date-fns-date')),
          deletedBy: decommissionedBy,
        }}
      />
      <Typography sx={{ fontWeight: 'bold' }}>{decommissioningReason}</Typography>
    </ErrorBanner>
  )
}

export default SystemDecommissionedBanner
